import React, { useCallback, useMemo, useState } from 'react';
import { Checkbox } from '@mui/material';

import { Column, ListingSelectionConfig } from './types';
import { grey } from '@mui/material/colors';

export const SELECTION_COLUMN_KEY = '__selection__';

interface UseListingSelectionResult<Data extends Record<string, any>, Context extends any = any> {
  selected: string[];
  selectionColumn: Column<Data, Context>;
  clearSelection: () => void;
}

export function useListingSelection<Data extends Record<string, any>, Context extends any = any>(
  rows: Data[],
  selection?: ListingSelectionConfig<Data>
): UseListingSelectionResult<Data, Context> {
  const getRowId = useCallback((row: Data) => selection?.getRowId?.(row) ?? String(row.id), [selection]);

  const isRowSelectable = useCallback((row: Data) => selection?.isRowSelectable?.(row) ?? true, [selection]);

  const [selected, setSelected] = useState<string[]>([]);

  const updateSelected = useCallback(
    (next: string[]) => {
      setSelected(next);
      selection?.onSelectionChange?.(next);
    },
    [selection]
  );

  const selectableIds = useMemo(() => rows.filter(isRowSelectable).map(getRowId), [rows, isRowSelectable, getRowId]);

  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.includes(id));
  const someSelected = !allSelected && selectableIds.some(id => selected.includes(id));

  const toggleRow = useCallback(
    (id: string) => {
      updateSelected(selected.includes(id) ? selected.filter(current => current !== id) : [...selected, id]);
    },
    [selected, updateSelected]
  );

  const toggleAll = useCallback(() => {
    updateSelected(allSelected ? [] : selectableIds);
  }, [allSelected, selectableIds, updateSelected]);

  const clearSelection = useCallback(() => updateSelected([]), [updateSelected]);

  const selectionColumn = useMemo<Column<Data, Context>>(
    () => ({
      fixed: 'left',
      header: '',
      renderKey: SELECTION_COLUMN_KEY,
      width: selection?.columnWidth ?? '3rem',
      headerStyles: {
        minHeight: '2.5rem',
        backgroundColor: grey[200],
      },
      cellStyles: {
        padding: '0.2rem 0.5rem',
        zIndex: 2,
        justifyContent: 'center',
      },
      renderHeadCell: () => (
        <Checkbox
          size="small"
          onChange={toggleAll}
          checked={allSelected}
          indeterminate={someSelected}
          slotProps={{ input: { 'aria-label': 'Select all rows' }, root: { sx: { padding: 0 } } }}
        />
      ),
      renderCell: ({ row }) => {
        if (!isRowSelectable(row)) return null;

        const id = getRowId(row);

        return (
          <Checkbox
            size="small"
            checked={selected.includes(id)}
            onChange={() => toggleRow(id)}
            slotProps={{ input: { 'aria-label': 'Select row' } }}
          />
        );
      },
    }),
    [selection, allSelected, someSelected, toggleAll, isRowSelectable, getRowId, selected, toggleRow]
  );

  return { selected, selectionColumn, clearSelection };
}
